import { DeleteOutlined, PercentageOutlined, PlusOutlined, RedoOutlined, SettingOutlined } from "@ant-design/icons";
import { Alert, Button, Card, InputNumber, Radio, Select, Table, Typography } from "antd";
import { useFormikContext } from "formik";
import { memo, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import styles from "../../styles/LateManagement.module.css";

const { Text } = Typography;

export const CALC_BY = { ACTUAL: "ActualPayRate", CUSTOM: "Custom" };
export const OVER_LIMIT_ACTIONS = ["MAX_PREVIOUS", "FIXED_AMOUNT", "HALF_DAY", "FULL_DAY", "LEAVE_DAY"];

const relink = (rows) =>
    rows.map((r, i) => ({ ...r, fromMinute: i === 0 ? 1 : (rows[i - 1].toMinute ?? 0) + 1 }));
const emptyTier = (prevTo = 0) => ({ fromMinute: prevTo + 1, toMinute: null, amount: null });

const nameOf = (opts, id) => {
    const o = (opts ?? []).find((x) => (x.value ?? x.id) === id);
    return o ? (o.label ?? o.name) : undefined;
};
/** แถวที่ save แล้วเท่านั้นที่มี _id จริง (แถวใหม่จะไม่มี id หรือเป็น "X...") */
const isSavedId = (v) => /^[a-f0-9]{24}$/i.test(v ?? "");
/** สร้าง options จากแถวที่ save แล้วในตารางรายได้/รายหัก: value = _id ของแถว, label = ชื่อ (ประเภท) */
const rowsToOptions = (rows, key, typeKey, nameOpts, typeOpts) =>
    (rows ?? []).reduce((acc, r) => {
        if (!r?.[key] || !isSavedId(r?.id)) return acc;
        const name = nameOf(nameOpts, r[key]) ?? r[key];
        const type = nameOf(typeOpts, r[typeKey]);
        acc.push({ value: r.id, label: type ? `${name} (${type})` : name });
        return acc;
    }, []);

/** Formik `validate` – runs next to the existing yup `validationSchema`. */
export const validateLateManagement = (values, amountId) => {
    const lm = values?.lateManagement;
    if (!lm || !amountId || lm.deductionTypeId !== amountId) return {};
    const errors = {};
    if (!lm.deductionId) errors.deductionId = "required";
    // if (lm.calculateDeductionBy === CALC_BY.ACTUAL && !lm.incomeIds?.length) errors.incomeIds = "required";
    if (lm.calculateDeductionBy === CALC_BY.CUSTOM) {
        const tiers = lm.tiers ?? [];
        if (!tiers.length) errors.tiers = "required";
        else {
            const rowErrors = tiers.map((t) => {
                const e = {};
                if (t.toMinute == null || t.toMinute < t.fromMinute) e.toMinute = "invalid";
                if (t.amount == null || t.amount < 0) e.amount = "invalid";
                return e;
            });
            if (rowErrors.some((e) => Object.keys(e).length)) errors.tiers = rowErrors;
        }
        if (lm.overLimitAction === "FIXED_AMOUNT" && (lm.overLimitAmount == null || lm.overLimitAmount < 0)) {
            errors.overLimitAmount = "invalid";
        }
    }
    return Object.keys(errors).length ? { lateManagement: errors } : {};
};

const Row = ({ label, children }) => (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(180px, 1fr) 2fr", alignItems: "center", gap: 16, padding: "10px 0" }}>
        <Text>{label}</Text>
        <div>{children}</div>
    </div>
);

/** noneId / amountId = ids of "No deductions" / "Amount deducted" (noWorkHoursN?.id / noWorkHoursA?.id). */
const LateManagementTab = ({ noneId, amountId, incomeNameOptions = [], deductionNameOptions = [], typeOptions = [] }) => {
    const { t } = useTranslation();
    const { values, errors, setFieldValue } = useFormikContext();
    const lm = values.lateManagement ?? {};
    const lmErr = errors.lateManagement ?? {};

    const incomeOptions = useMemo(
        () => rowsToOptions(values.incomes, "incomeId", "incomeTypeId", incomeNameOptions, typeOptions),
        [values.incomes, incomeNameOptions, typeOptions]
    );
    const deductionOptions = useMemo(
        () => rowsToOptions(values.deductions, "deductionId", "deductionTypeId", deductionNameOptions, typeOptions),
        [values.deductions, deductionNameOptions, typeOptions]
    );

    const mode = lm.deductionTypeId === amountId ? "AMOUNT" : "NONE";
    // ต้องมีรายได้และรายหักที่บันทึกแล้วอย่างน้อยอย่างละ 1 แถว ถึงจะเลือก "หักเป็นเงิน" ได้
    const hasSavedRows = incomeOptions.length > 0 && deductionOptions.length > 0;
    const isAmount = mode === "AMOUNT";
    const isCustom = lm.calculateDeductionBy === CALC_BY.CUSTOM;
    const tiers = lm.tiers ?? [];
    const lastTo = tiers[tiers.length - 1]?.toMinute;
    const overLimitOptions = useMemo(
        () => OVER_LIMIT_ACTIONS.map((value) => ({ value, label: t(`employmentType.lateManagement.overLimit.${value}`) })),
        [t]
    );
    const maxPrevious = useMemo(() => tiers.reduce((m, t) => Math.max(m, t.amount ?? 0), 0), [tiers]);

    const onOverLimitAction = (v) => {
        set("overLimitAction", v);
        if (v !== "FIXED_AMOUNT") set("overLimitAmount", undefined);
    };
    const set = useCallback((field, v) => setFieldValue(`lateManagement.${field}`, v), [setFieldValue]);
    const setTiers = useCallback((rows) => set("tiers", relink(rows)), [set]);

    // ล้างค่าที่เลือกไว้ ถ้าแถวนั้นถูกลบออกจากตารางรายได้/รายหัก
    useEffect(() => {
        const ids = lm.incomeIds ?? [];
        const valid = new Set(incomeOptions.map((o) => o.value));
        const kept = ids.filter((id) => valid.has(id));
        if (kept.length !== ids.length) set("incomeIds", kept);
        if (lm.deductionId && !deductionOptions.some((o) => o.value === lm.deductionId)) {
            set("deductionId", undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [incomeOptions, deductionOptions]);

    const onMode = (e) => {
        if (e.target.value === "AMOUNT") {
            setFieldValue("lateManagement", {
                ...lm,
                deductionTypeId: amountId,
                calculateDeductionBy: lm.calculateDeductionBy ?? CALC_BY.ACTUAL,
                incomeIds: lm.incomeIds ?? [],
                tiers: lm.tiers ?? [],
            });
        } else {
            // reset so no stale / half-filled data is sent to the BE
            setFieldValue("lateManagement", { deductionTypeId: noneId, incomeIds: [], tiers: [] });
        }
    };
    const onCalcBy = (e) => {
        set("calculateDeductionBy", e.target.value);
        if (e.target.value === CALC_BY.CUSTOM && !tiers.length) setTiers([emptyTier()]);
    };
    const patchTier = (i, patch) => setTiers(tiers.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));

    const columns = [
        { title: t("employmentType.lateManagement.colSeq"), width: 70, align: "center", render: (_v, _r, i) => i + 1 },
        {
            title: t("employmentType.lateManagement.colLateMinutes"),
            align: "center",
            render: (_v, r, i) => (
                <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                    <InputNumber value={r.fromMinute} disabled style={{ width: 90 }} />
                    -
                    <InputNumber
                        value={r.toMinute}
                        min={r.fromMinute}
                        precision={0}
                        style={{ width: 90 }}
                        status={lmErr.tiers?.[i]?.toMinute ? "error" : undefined}
                        onChange={(v) => patchTier(i, { toMinute: v })}
                    />
                </span>
            ),
        },
        {
            title: t("employmentType.lateManagement.colAmount"),
            align: "center",
            render: (_v, r, i) => (
                <InputNumber
                    value={r.amount}
                    min={0}
                    precision={2}
                    style={{ width: 140 }}
                    status={lmErr.tiers?.[i]?.amount ? "error" : undefined}
                    onChange={(v) => patchTier(i, { amount: v })}
                />
            ),
        },
        {
            title: t("employmentType.lateManagement.colAction"),
            width: 90,
            align: "center",
            render: (_v, _r, i) => (
                <Button type="text" danger title={t("employmentType.lateManagement.deleteRange")} aria-label={t("employmentType.lateManagement.deleteRange")} icon={<DeleteOutlined />} disabled={tiers.length === 1} onClick={() => setTiers(tiers.filter((_x, idx) => idx !== i))} />
            ),
        },
    ];

    return (
        <Card>
            <Row label={t("employmentType.lateManagement.deductionMode")}>
                <Radio.Group
                    optionType="button"
                    buttonStyle="solid"
                    value={mode}
                    onChange={onMode}
                    options={[
                        { label: t("employmentType.lateManagement.noDeduction"), value: "NONE" },
                        // ถ้าเคยตั้งเป็น "หักเป็นเงิน" ไว้แล้ว ไม่ disable เพื่อไม่ให้ค่าเดิมค้าง
                        { label: t("employmentType.lateManagement.amountDeduction"), value: "AMOUNT", disabled: !hasSavedRows && !isAmount },
                    ]}
                />
            </Row>

            {!hasSavedRows && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginTop: 8, marginBottom: 8 }}
                    message={t("employmentType.lateManagement.requireSavedIncomeDeduction")}
                />
            )}

            {isAmount && (
                <>
                    {/* <Row label={t("employmentType.lateManagement.incomeTypes")}>
                        <Select
                            mode="multiple"
                            style={{ width: "100%" }}
                            options={incomeOptions}
                            notFoundContent={t("employmentType.lateManagement.noSavedIncome")}
                            value={lm.incomeIds ?? []}
                            onChange={(v) => set("incomeIds", v)}
                            optionFilterProp="label"
                            status={lmErr.incomeIds ? "error" : undefined}
                            maxTagCount="responsive"
                        />
                    </Row> */}

                    <Row label={t("employmentType.lateManagement.deductionItem")}>
                        <Select
                            showSearch
                            style={{ width: "100%" }}
                            options={deductionOptions}
                            notFoundContent={t("employmentType.lateManagement.noSavedDeduction")}
                            value={lm.deductionId}
                            onChange={(v) => set("deductionId", v)}
                            optionFilterProp="label"
                            status={lmErr.deductionId ? "error" : undefined}
                        />
                    </Row>

                    <Row label={t("employmentType.lateManagement.calculateBy")}>
                        <Radio.Group
                            optionType="button"
                            buttonStyle="solid"
                            value={lm.calculateDeductionBy}
                            onChange={onCalcBy}
                            options={[
                                { label: <><PercentageOutlined /> {t("employmentType.lateManagement.byIncomeRatio")}</>, value: CALC_BY.ACTUAL },
                                { label: <><SettingOutlined /> {t("employmentType.lateManagement.custom")}</>, value: CALC_BY.CUSTOM },
                            ]}
                        />
                    </Row>

                    <Row label={t("employmentType.lateManagement.exemptionMinutes")}>
                        <InputNumber
                            min={0}
                            precision={0}
                            style={{ width: "100%" }}
                            addonAfter={t("unit.minute")}
                            value={lm.lateExemptionMinutes ?? 0}
                            onChange={(v) => set("lateExemptionMinutes", v ?? 0)}
                        />
                    </Row>

                    {isCustom && (
                        <Card
                            size="small"
                            style={{ marginTop: 12 }}
                            title={<div><div>{t("employmentType.lateManagement.customTitle")}</div><Text type="secondary" style={{ fontSize: 12 }}>{t("employmentType.lateManagement.customHint")}</Text></div>}
                            extra={<Button icon={<PlusOutlined />} onClick={() => setTiers([...tiers, emptyTier(lastTo)])} disabled={lastTo == null}>{t("employmentType.lateManagement.addRange")}</Button>}
                        >
                            <Table
                                className={styles.tierTable}
                                size="small"
                                bordered
                                pagination={false}
                                rowKey={(_r, i) => i}
                                dataSource={tiers}
                                columns={columns}
                                summary={() =>
                                    lastTo == null ? null : (
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0} align="center"><b>{tiers.length + 1}</b></Table.Summary.Cell>
                                            <Table.Summary.Cell index={1} align="center"><b>{t("employmentType.lateManagement.moreThan", { minutes: lastTo })}</b></Table.Summary.Cell>
                                            <Table.Summary.Cell index={2} align="center">
                                                <span style={{ display: "inline-flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
                                                    {t("employmentType.lateManagement.thenAction")}
                                                    <Select
                                                        allowClear
                                                        style={{ width: 300 }}
                                                        popupMatchSelectWidth={false}
                                                        options={overLimitOptions}
                                                        value={lm.overLimitAction}
                                                        onChange={onOverLimitAction}
                                                    />
                                                    {lm.overLimitAction === "FIXED_AMOUNT" && (
                                                        <InputNumber
                                                            min={0}
                                                            precision={2}
                                                            style={{ width: 140 }}
                                                            addonAfter={t("unit.baht")}
                                                            value={lm.overLimitAmount}
                                                            status={lmErr.overLimitAmount ? "error" : undefined}
                                                            onChange={(v) => set("overLimitAmount", v)}
                                                        />
                                                    )}
                                                    {lm.overLimitAction === "MAX_PREVIOUS" && <Text type="secondary">= {maxPrevious.toFixed(2)} {t("unit.baht")}</Text>}
                                                </span>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={3} align="center">
                                                <Button type="text" danger title={t("employmentType.lateManagement.reset")} aria-label={t("employmentType.lateManagement.reset")} icon={<RedoOutlined />} disabled={!lm.overLimitAction} onClick={() => set("overLimitAction", undefined)} />
                                            </Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    )
                                }
                            />
                        </Card>
                    )}
                </>
            )}
        </Card>
    );
};

export default memo(LateManagementTab);